import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Upload,
  Download,
  Percent,
  Trash2,
  Truck,
  RefreshCw,
  Plus,
  Edit3,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileSpreadsheet,
  ArrowRightLeft,
  X,
  TrendingUp,
  TrendingDown,
  Calculator,
  History,
  RotateCcw,
  Clock,
  User,
  Printer,
  FileText,
  FileDown,
  Loader2
} from "lucide-react";
import * as XLSX from "xlsx";
import XLSXStyle from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShippingPage = () => {
  usePageTitle("Shipping Rates");
  const { token } = useAuth();
  
  // Data state
  const [shipping, setShipping] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadTime, setLoadTime] = useState(null); // Track API response time in ms
  
  // Pagination & Sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortField, setSortField] = useState("shipment");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterShipment, setFilterShipment] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [availableFilters, setAvailableFilters] = useState({ shipments: [], countries: [] });
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showErrorPanel, setShowErrorPanel] = useState(false);
  const [showBulkPriceDialog, setShowBulkPriceDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Upload state
  const [uploadAnalysis, setUploadAnalysis] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  
  // Background upload state
  const [backgroundTaskId, setBackgroundTaskId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressMessage, setUploadProgressMessage] = useState("");
  
  // History state
  const [priceHistory, setPriceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [rollingBack, setRollingBack] = useState(null);
  
  // Export/Print state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    format: "excel",
    filterType: "current", // current, service, country, both, selected
    service: "",
    country: "",
    increasePercent: "",
    selectedOnly: false,
    loading: false
  });
  
  // Rate Price History state
  const [showRateHistoryDialog, setShowRateHistoryDialog] = useState(false);
  const [rateHistoryData, setRateHistoryData] = useState(null);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);
  
  // Edit state
  const [editingShipping, setEditingShipping] = useState(null);
  const [priceAdjustment, setPriceAdjustment] = useState("");
  const [adjustingPrices, setAdjustingPrices] = useState(false);
  const [newShipping, setNewShipping] = useState({
    shipment: "",
    country: "",
    weight_range: "",
    cost: "",
    suggested_usd_price: ""
  });
  
  // Bulk price adjustment state
  const [bulkPriceData, setBulkPriceData] = useState({
    service: "",
    country: "",
    percentage: "",
    preview: [],
    affectedCount: 0,
    loading: false
  });
  
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch shipping data with server-side pagination
  const fetchShipping = useCallback(async () => {
    try {
      setLoading(true);
      const startTime = performance.now(); // Start timing
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        sort_field: sortField,
        sort_order: sortOrder
      });
      
      if (searchTerm) params.set("search", searchTerm);
      if (filterShipment) params.set("shipment", filterShipment);
      if (filterCountry) params.set("country", filterCountry);
      
      const response = await fetch(`${BACKEND_URL}/api/shipping/paginated?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const endTime = performance.now(); // End timing
      setLoadTime(Math.round(endTime - startTime)); // Store load time in ms
      
      if (response.ok) {
        const data = await response.json();
        setShipping(data.shipping || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
        setAvailableFilters(data.filters || { shipments: [], countries: [] });
      } else {
        toast.error("Failed to load shipping rates");
      }
    } catch (error) {
      toast.error("Failed to load shipping rates");
      setLoadTime(null);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, pageSize, sortField, sortOrder, searchTerm, filterShipment, filterCountry]);

  useEffect(() => {
    fetchShipping();
  }, [fetchShipping]);

  // Debounced search
  const handleSearchChange = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 300);
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(shipping.map(s => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
      setSelectAll(false);
    }
  };

  // Edit handlers
  const handleEditSelected = () => {
    if (selectedIds.length !== 1) {
      toast.error("Select exactly one rate to edit");
      return;
    }
    const item = shipping.find(s => s.id === selectedIds[0]);
    if (item) {
      setEditingShipping({ ...item });
      setShowEditDialog(true);
    }
  };

  const handleSaveShipping = async () => {
    if (!editingShipping) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/${editingShipping.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editingShipping)
      });

      if (response.ok) {
        toast.success("Shipping rate updated");
        setShowEditDialog(false);
        setEditingShipping(null);
        setSelectedIds([]);
        fetchShipping();
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    }
  };

  // Price adjustment
  const handlePriceAdjust = async () => {
    const percentage = parseFloat(priceAdjustment);
    if (isNaN(percentage) || percentage === 0) {
      toast.error("Please enter a non-zero percentage (e.g. 10 or -5)");
      return;
    }

    const scopeMsg = selectedIds.length > 0
      ? `${selectedIds.length} selected rate(s)`
      : `ALL ${total.toLocaleString()} shipping rates`;
    const direction = percentage > 0 ? "increase" : "decrease";
    if (!window.confirm(
      `Adjust shipping rates by ${direction === "increase" ? "+" : ""}${percentage}%?\n\n` +
      `Scope: ${scopeMsg}\n\n` +
      `Original rates are saved — click "Reset Original" to revert.`
    )) return;

    setAdjustingPrices(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/adjust-prices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          percentage,
          ids: selectedIds.length > 0 ? selectedIds : null
        })
      });

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || data.message || `Adjustment failed (HTTP ${response.status})`);
        return;
      }
      toast.success(data.message || `Adjusted by ${percentage}%`);
      await fetchShipping();
      setPriceAdjustment("");
    } catch (error) {
      toast.error("Price adjustment failed: " + (error.message || "network error"));
    } finally {
      setAdjustingPrices(false);
    }
  };

  // Trigger bulk delete confirmation
  const triggerBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Select rates to delete");
      return;
    }
    setDeleteTarget({ type: "bulk", count: selectedIds.length });
    setShowDeleteDialog(true);
  };

  // Execute bulk delete after password confirmation
  const executeBulkDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });

      const data = await response.json();
      toast.success(data.message);
      setSelectedIds([]);
      setSelectAll(false);
      fetchShipping();
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // File upload - Always uses background processing for reliability
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadProgressMessage("Uploading file...");
    
    const formData = new FormData();
    formData.append("file", file);

    // Retry logic for production reliability
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          setUploadProgressMessage(`Retrying upload (attempt ${attempt + 1})...`);
          await new Promise(r => setTimeout(r, 1000)); // Wait before retry
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout for initial request

        const response = await fetch(`${BACKEND_URL}/api/shipping/upload-background`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        // Handle non-OK responses
        if (!response.ok) {
          let errorMsg = `Server error (${response.status})`;
          try {
            const text = await response.text();
            if (text) {
              const errorData = JSON.parse(text);
              errorMsg = errorData.detail || errorMsg;
            }
          } catch (parseErr) {
            console.warn("Could not parse error response:", parseErr);
          }
          throw new Error(errorMsg);
        }
        
        // Parse success response
        let data;
        try {
          const text = await response.text();
          data = JSON.parse(text);
        } catch (parseErr) {
          throw new Error("Invalid response from server");
        }
        
        if (data.task_id) {
          setBackgroundTaskId(data.task_id);
          setUploadProgressMessage("Processing file...");
          pollUploadProgress(data.task_id, 0); // Start polling with retry counter
          return; // Success - exit retry loop
        } else {
          throw new Error("No task ID returned from server");
        }

      } catch (error) {
        lastError = error;
        console.error(`Upload attempt ${attempt + 1} failed:`, error);
        
        // Don't retry on abort (user cancelled) or certain errors
        if (error.name === 'AbortError') {
          toast.error("Upload timed out. Please try again.");
          break;
        }
        
        // Continue to next retry attempt
        if (attempt === maxRetries) {
          break; // No more retries
        }
      }
    }

    // All retries failed
    setUploading(false);
    toast.error(lastError?.message || "Upload failed. Please try again.");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  
  // Handle upload response (used by background upload completion)
  const handleUploadResponse = (isOk, data) => {
    if (isOk) {
      setUploadAnalysis(data);
      setUploadErrors(data.errors || []);
      
      if (data.duplicates_with_price_change > 0) {
        setSelectedDuplicates((data.duplicate_details || []).map(d => d.id));
        setShowUploadDialog(false);
        setShowDuplicateDialog(true);
      } else if (data.new_records > 0) {
        confirmUpload(data.new_records_data || [], false, [], []);
      } else if (data.errors && data.errors.length > 0) {
        setShowUploadDialog(false);
        setShowErrorPanel(true);
        toast.warning(`${data.errors.length} errors found during processing`);
      } else {
        toast.info("No new rates to upload");
        setShowUploadDialog(false);
      }
    } else {
      const errorMessage = data?.detail || data?.message || "Upload failed";
      toast.error(errorMessage);
      console.error("Upload error:", data);
    }
  };
  
  // Poll for background upload progress with retry logic
  const pollUploadProgress = async (taskId, retryCount = 0) => {
    const maxPollRetries = 5;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for polling
      
      const response = await fetch(`${BACKEND_URL}/api/shipping/upload-status/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Status check failed (${response.status})`);
      }
      
      let task;
      try {
        const text = await response.text();
        task = JSON.parse(text);
      } catch (parseErr) {
        throw new Error("Invalid status response");
      }
      
      if (task.status === "completed") {
        setUploadProgress(100);
        setUploadProgressMessage("Complete!");
        setUploading(false);
        setBackgroundTaskId(null);
        
        if (task.result) {
          handleUploadResponse(true, task.result);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
        
      } else if (task.status === "failed") {
        setUploading(false);
        setBackgroundTaskId(null);
        toast.error(task.error || "Upload processing failed");
        if (fileInputRef.current) fileInputRef.current.value = "";
        
      } else {
        // Still processing - update progress and continue polling
        setUploadProgress(task.progress || 0);
        setUploadProgressMessage(task.progress_message || "Processing...");
        
        // Poll again after 1.5 seconds
        setTimeout(() => pollUploadProgress(taskId, 0), 1500);
      }
      
    } catch (error) {
      console.error("Error polling upload status:", error);
      
      if (retryCount < maxPollRetries) {
        // Retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
        setUploadProgressMessage(`Checking status... (retry ${retryCount + 1})`);
        setTimeout(() => pollUploadProgress(taskId, retryCount + 1), delay);
      } else {
        // Max retries reached
        setUploading(false);
        setBackgroundTaskId(null);
        toast.error("Lost connection to server. Please check your uploads and try again.");
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    }
  };

  // File upload - Step 2: Confirm
  const confirmUpload = async (newRecords, updateDuplicates, duplicateIds, duplicateDetails) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/upload-confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          new_records: newRecords,
          update_duplicates: updateDuplicates,
          duplicate_ids: duplicateIds,
          duplicate_details: duplicateDetails
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowDuplicateDialog(false);
        setUploadAnalysis(null);
        fetchShipping();
        
        if (uploadErrors.length > 0) {
          setShowErrorPanel(true);
        }
      } else {
        toast.error(data.detail || "Confirmation failed");
      }
    } catch (error) {
      toast.error("Confirmation failed");
    }
  };

  const handleDuplicateSelect = (id, checked) => {
    if (checked) {
      setSelectedDuplicates(prev => [...prev, id]);
    } else {
      setSelectedDuplicates(prev => prev.filter(i => i !== id));
    }
  };

  const handleConfirmDuplicates = async (updateSelected) => {
    if (!uploadAnalysis) return;
    
    const duplicatesToUpdate = updateSelected 
      ? uploadAnalysis.duplicate_details.filter(d => selectedDuplicates.includes(d.id))
      : [];
    
    await confirmUpload(
      uploadAnalysis.new_records_data,
      updateSelected,
      selectedDuplicates,
      duplicatesToUpdate
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/download-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shipping_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleAddShipping = async () => {
    if (!newShipping.shipment || !newShipping.country) {
      toast.error("Shipment and Country are required");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newShipping)
      });

      if (response.ok) {
        toast.success("Shipping rate added");
        setShowAddDialog(false);
        setNewShipping({ shipment: "", country: "", weight_range: "", cost: "", suggested_usd_price: "" });
        fetchShipping();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add shipping rate");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterShipment("");
    setFilterCountry("");
    setCurrentPage(1);
  };

  // Bulk price adjustment - Preview
  const handleBulkPricePreview = async () => {
    if (!bulkPriceData.percentage) {
      toast.error("Enter a percentage value");
      return;
    }
    if (!bulkPriceData.service && !bulkPriceData.country) {
      toast.error("Select at least one filter (Service or Country)");
      return;
    }

    setBulkPriceData(prev => ({ ...prev, loading: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/bulk-price-adjust`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          percentage: parseFloat(bulkPriceData.percentage),
          service: bulkPriceData.service,
          country: bulkPriceData.country,
          preview: true
        })
      });

      const data = await response.json();
      if (response.ok) {
        setBulkPriceData(prev => ({
          ...prev,
          preview: data.preview || [],
          affectedCount: data.affected_count || 0,
          loading: false
        }));
      } else {
        toast.error(data.detail || "Preview failed");
        setBulkPriceData(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      toast.error("Preview failed");
      setBulkPriceData(prev => ({ ...prev, loading: false }));
    }
  };

  // Bulk price adjustment - Apply
  const handleBulkPriceApply = async () => {
    if (!bulkPriceData.percentage) {
      toast.error("Enter a percentage value");
      return;
    }
    if (!bulkPriceData.service && !bulkPriceData.country) {
      toast.error("Select at least one filter");
      return;
    }

    if (!window.confirm(`Are you sure you want to adjust ${bulkPriceData.affectedCount} shipping rates by ${bulkPriceData.percentage}%?`)) {
      return;
    }

    setBulkPriceData(prev => ({ ...prev, loading: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/bulk-price-adjust`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          percentage: parseFloat(bulkPriceData.percentage),
          service: bulkPriceData.service,
          country: bulkPriceData.country,
          preview: false
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowBulkPriceDialog(false);
        setBulkPriceData({
          service: "",
          country: "",
          percentage: "",
          preview: [],
          affectedCount: 0,
          loading: false
        });
        fetchShipping();
      } else {
        toast.error(data.detail || "Adjustment failed");
        setBulkPriceData(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      toast.error("Adjustment failed");
      setBulkPriceData(prev => ({ ...prev, loading: false }));
    }
  };

  // Price History Functions
  const fetchPriceHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/price-history?page=${page}&page_size=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.history || []);
        setHistoryTotal(data.total || 0);
        setHistoryTotalPages(data.total_pages || 1);
        setHistoryPage(page);
      } else {
        toast.error("Failed to load price history");
      }
    } catch (error) {
      toast.error("Failed to load price history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    setShowHistoryDialog(true);
    fetchPriceHistory(1);
  };

  const handleRollback = async (historyId, entry) => {
    if (!window.confirm(`Are you sure you want to rollback this ${entry.summary?.percentage > 0 ? 'increase' : 'decrease'} of ${Math.abs(entry.summary?.percentage)}%?\n\nThis will reverse the price changes for ${entry.summary?.records_affected} rates.`)) {
      return;
    }

    setRollingBack(historyId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/rollback/${historyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchPriceHistory(historyPage);
        fetchShipping();
      } else {
        toast.error(data.detail || "Rollback failed");
      }
    } catch (error) {
      toast.error("Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  const [deletingUpload, setDeletingUpload] = useState(null);
  
  const handleDeleteUpload = async (historyId, entry) => {
    const summary = entry.summary || {};
    const filename = summary.filename || "this upload";
    const inserted = summary.inserted || 0;
    
    if (!window.confirm(`Are you sure you want to DELETE this upload?\n\nFile: ${filename}\nRecords to delete: ${inserted} shipping rates\n\n⚠️ This action cannot be undone!`)) {
      return;
    }

    setDeletingUpload(historyId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/delete-upload/${historyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchPriceHistory(historyPage);
        fetchShipping();
      } else {
        toast.error(data.detail || "Delete failed");
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setDeletingUpload(null);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch Rate Price History
  const fetchRateHistory = async (rateId, rate) => {
    setRateHistoryLoading(true);
    setShowRateHistoryDialog(true);
    setRateHistoryData({ rate, current: null, history: [] });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/rate-history/${rateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRateHistoryData({
          rate: data.rate || rate,
          current: data.current,
          history: data.history || []
        });
      } else {
        toast.error("Failed to load price history");
      }
    } catch (error) {
      toast.error("Failed to load price history");
    } finally {
      setRateHistoryLoading(false);
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Export/Print Functions
  const fetchExportData = async () => {
    try {
      // If exporting selected only, use the selected items
      if (exportConfig.selectedOnly && selectedIds.length > 0) {
        const selectedData = shipping.filter(s => selectedIds.includes(s.id));
        return selectedData;
      }
      
      // Otherwise fetch from server with filters
      const params = new URLSearchParams({
        page: "1",
        page_size: "50000", // Get all matching records
        sort_field: sortField,
        sort_order: sortOrder
      });
      
      // Apply export filters
      if (exportConfig.filterType === "service" && exportConfig.service) {
        params.set("shipment", exportConfig.service);
      } else if (exportConfig.filterType === "country" && exportConfig.country) {
        params.set("country", exportConfig.country);
      } else if (exportConfig.filterType === "both") {
        if (exportConfig.service) params.set("shipment", exportConfig.service);
        if (exportConfig.country) params.set("country", exportConfig.country);
      } else if (exportConfig.filterType === "current") {
        // Use current page filters
        if (filterShipment) params.set("shipment", filterShipment);
        if (filterCountry) params.set("country", filterCountry);
        if (searchTerm) params.set("search", searchTerm);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/shipping/paginated?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.shipping || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching export data:", error);
      return [];
    }
  };

  const applyPriceIncrease = (data, percentage) => {
    if (!percentage || isNaN(parseFloat(percentage))) return data;
    
    const multiplier = 1 + (parseFloat(percentage) / 100);
    return data.map(item => ({
      ...item,
      cost: item.cost ? String(Math.round(parseFloat(String(item.cost).replace(",", "")) * multiplier * 100) / 100) : item.cost,
      suggested_usd_price: item.suggested_usd_price ? String(Math.round(parseFloat(String(item.suggested_usd_price).replace(",", "")) * multiplier * 100) / 100) : item.suggested_usd_price
    }));
  };

  const handleExportExcel = async () => {
    setExportConfig(prev => ({ ...prev, loading: true }));
    
    try {
      let data = await fetchExportData();
      
      if (data.length === 0) {
        toast.error("No data to export");
        setExportConfig(prev => ({ ...prev, loading: false }));
        return;
      }
      
      // Apply price increase if specified
      if (exportConfig.increasePercent) {
        data = applyPriceIncrease(data, exportConfig.increasePercent);
      }
      
      // Build styled workbook matching the screen design
      // (emerald title, slate column headers, banded rows, amber price cells)
      const wb = XLSXStyle.utils.book_new();

      const titleStyle = {
        font: { name: "Arial", sz: 16, bold: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "047857" } }, // emerald-700
        alignment: { horizontal: "left", vertical: "center" },
      };
      const titleDateStyle = {
        font: { name: "Arial", sz: 11, italic: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "047857" } },
        alignment: { horizontal: "right", vertical: "center" },
      };
      const headerStyle = {
        font: { name: "Arial", sz: 10, bold: true, color: { rgb: "1E293B" } },
        fill: { patternType: "solid", fgColor: { rgb: "ECFDF5" } }, // emerald-50
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "A7F3D0" } },
          bottom: { style: "thin", color: { rgb: "A7F3D0" } },
          left: { style: "thin", color: { rgb: "A7F3D0" } },
          right: { style: "thin", color: { rgb: "A7F3D0" } },
        },
      };
      const cellBase = {
        font: { name: "Arial", sz: 10, color: { rgb: "1E293B" } },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "E2E8F0" } },
          bottom: { style: "thin", color: { rgb: "E2E8F0" } },
          left: { style: "thin", color: { rgb: "E2E8F0" } },
          right: { style: "thin", color: { rgb: "E2E8F0" } },
        },
      };
      const cellAlt = {
        ...cellBase,
        fill: { patternType: "solid", fgColor: { rgb: "F8FAFC" } },
      };
      const priceCell = (base) => ({
        ...base,
        font: { ...base.font, bold: true, color: { rgb: "B45309" } },
        alignment: { horizontal: "right", vertical: "center" },
      });

      const headers = ["S.No", "Service", "Country", "Weight Range", "Cost (₹)", "USD Price"];
      const wsData = [];
      const currentDate = new Date().toISOString().slice(0, 10);
      wsData.push([`Shipping Rates`, "", "", "", "", `Generated: ${currentDate}`]);
      wsData.push(headers);
      data.forEach((item, idx) => {
        wsData.push([
          idx + 1,
          item.shipment || "",
          item.country || "",
          item.weight_range || "",
          item.cost || "",
          item.suggested_usd_price || "",
        ]);
      });

      const ws = XLSXStyle.utils.aoa_to_sheet(wsData);

      // Style title row
      const totalCols = headers.length;
      ws[XLSXStyle.utils.encode_cell({ r: 0, c: 0 })].s = titleStyle;
      ws[XLSXStyle.utils.encode_cell({ r: 0, c: totalCols - 1 })] = { v: `Generated: ${currentDate}`, t: "s", s: titleDateStyle };
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 2 } }];

      // Style header row
      for (let c = 0; c < totalCols; c++) {
        const ref = XLSXStyle.utils.encode_cell({ r: 1, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }

      // Style data rows
      for (let r = 2; r < wsData.length; r++) {
        const base = (r - 2) % 2 === 0 ? cellBase : cellAlt;
        for (let c = 0; c < totalCols; c++) {
          const ref = XLSXStyle.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { v: "", t: "s" };
          ws[ref].s = (c === 4 || c === 5) ? priceCell(base) : base;
        }
      }

      ws["!cols"] = [
        { wch: 6 },   // S.No
        { wch: 18 },  // Service
        { wch: 22 },  // Country
        { wch: 18 },  // Weight Range
        { wch: 14 },  // Cost
        { wch: 14 },  // USD Price
      ];
      ws["!rows"] = wsData.map((_, idx) => {
        if (idx === 0) return { hpt: 28 };
        if (idx === 1) return { hpt: 22 };
        return { hpt: 18 };
      });
      ws["!freeze"] = { xSplit: 0, ySplit: 2 };

      XLSXStyle.utils.book_append_sheet(wb, ws, "Shipping Rates");
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      let filename = `Shipping_Rates_${dateStr}`;
      if (exportConfig.service) filename += `_${exportConfig.service}`;
      if (exportConfig.country) filename += `_${exportConfig.country}`;
      if (exportConfig.increasePercent) filename += `_+${exportConfig.increasePercent}pct`;
      filename += ".xlsx";
      
      // Save file (use styled writer)
      const excelBuffer = XLSXStyle.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);
      
      toast.success(`Exported ${data.length} rates to Excel (styled)`);
      setShowExportDialog(false);
    } catch (error) {
      toast.error("Export failed");
      console.error("Export error:", error);
    } finally {
      setExportConfig(prev => ({ ...prev, loading: false }));
    }
  };

  const handleExportPDF = async () => {
    setExportConfig(prev => ({ ...prev, loading: true }));
    
    try {
      let data = await fetchExportData();
      
      if (data.length === 0) {
        toast.error("No data to export");
        setExportConfig(prev => ({ ...prev, loading: false }));
        return;
      }
      
      // Apply price increase if specified
      if (exportConfig.increasePercent) {
        data = applyPriceIncrease(data, exportConfig.increasePercent);
      }
      
      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(16, 185, 129); // Emerald color
      doc.text("EAC - Shipping Price List", pageWidth / 2, 15, { align: "center" });
      
      // Subtitle with filters
      doc.setFontSize(10);
      doc.setTextColor(100);
      let subtitle = `Generated: ${formatDMY(new Date())}`;
      if (exportConfig.service) subtitle += ` | Service: ${exportConfig.service}`;
      if (exportConfig.country) subtitle += ` | Country: ${exportConfig.country}`;
      if (exportConfig.increasePercent) subtitle += ` | Price Increase: +${exportConfig.increasePercent}%`;
      doc.text(subtitle, pageWidth / 2, 22, { align: "center" });
      
      // Total count
      doc.setFontSize(9);
      doc.text(`Total Rates: ${data.length}`, pageWidth / 2, 28, { align: "center" });
      
      // Table data
      const tableData = data.map((item, idx) => [
        idx + 1,
        item.shipment || "",
        item.country || "",
        item.weight_range || "",
        item.cost || "",
        item.suggested_usd_price || ""
      ]);
      
      // Generate table using autoTable
      autoTable(doc, {
        startY: 32,
        head: [["#", "Service", "Country", "Weight Range", "Cost (₹)", "USD Price"]],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [16, 185, 129],
          textColor: 255,
          fontSize: 9,
          fontStyle: "bold"
        },
        bodyStyles: {
          fontSize: 8
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25, halign: "right" },
          5: { cellWidth: 25, halign: "right" }
        },
        margin: { top: 32, left: 10, right: 10 },
        didDrawPage: (data) => {
          // Footer with page number
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: "center" }
          );
        }
      });
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      let filename = `Shipping_Rates_${dateStr}`;
      if (exportConfig.service) filename += `_${exportConfig.service}`;
      if (exportConfig.country) filename += `_${exportConfig.country}`;
      if (exportConfig.increasePercent) filename += `_+${exportConfig.increasePercent}pct`;
      filename += ".pdf";
      
      doc.save(filename);
      
      toast.success(`Exported ${data.length} rates to PDF`);
      setShowExportDialog(false);
    } catch (error) {
      toast.error("Export failed");
      console.error("Export error:", error);
    } finally {
      setExportConfig(prev => ({ ...prev, loading: false }));
    }
  };

  const handleExport = () => {
    if (exportConfig.format === "excel") {
      handleExportExcel();
    } else {
      handleExportPDF();
    }
  };

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" 
        ? <ArrowUp className="w-3 h-3 text-blue-600" /> 
        : <ArrowDown className="w-3 h-3 text-blue-600" />;
    }
    return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="shipping-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Shipping Price List</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              {total.toLocaleString()} total rates • Page {currentPage} of {totalPages}
              {selectedIds.length > 0 && ` • ${selectedIds.length} selected`}
              {loadTime !== null && !loading && (
                <Badge 
                  variant="outline" 
                  className={`ml-2 text-xs ${loadTime < 200 ? 'border-green-400 text-green-600 bg-green-50' : loadTime < 500 ? 'border-yellow-400 text-yellow-600 bg-yellow-50' : 'border-red-400 text-red-600 bg-red-50'}`}
                  data-testid="load-time-badge"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {loadTime}ms
                </Badge>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddDialog(true)} className="gap-2 border-blue-300 hover:bg-blue-50">
            <Plus className="w-4 h-4 text-blue-500" /> Add Rate
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="gap-2 border-orange-300 hover:bg-orange-50">
            <Upload className="w-4 h-4 text-orange-500" /> Upload
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBulkPriceDialog(true)} className="gap-2 border-purple-300 hover:bg-purple-50" data-testid="bulk-price-btn">
            <Calculator className="w-4 h-4 text-purple-500" /> Bulk Price Adjust
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowExportDialog(true)} className="gap-2 border-teal-300 hover:bg-teal-50" data-testid="export-btn">
            <Printer className="w-4 h-4 text-teal-500" /> Print / Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleOpenHistory} className="gap-2 border-indigo-300 hover:bg-indigo-50" data-testid="price-history-btn">
            <History className="w-4 h-4 text-indigo-500" /> Price History
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-2 border-emerald-300 hover:bg-emerald-50">
            <Download className="w-4 h-4 text-emerald-500" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={fetchShipping} className="gap-2">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {uploadErrors.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowErrorPanel(true)} className="gap-2 border-red-300 hover:bg-red-50">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Errors ({uploadErrors.length})
            </Button>
          )}
        </div>
      </div>

      {/* Filters & Controls */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search rates..."
                defaultValue={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
            </div>

            <Select value={filterShipment || "ALL"} onValueChange={(v) => { setFilterShipment(v === "ALL" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue placeholder="All Services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Services</SelectItem>
                {availableFilters.shipments.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterCountry || "ALL"} onValueChange={(v) => { setFilterCountry(v === "ALL" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Countries</SelectItem>
                {availableFilters.countries.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(searchTerm || filterShipment || filterCountry) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-slate-500">
                <X className="w-3 h-3" /> Clear
              </Button>
            )}

            <div className="flex-1" />

            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Percent className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <Input
                type="number"
                placeholder="Adjust %"
                value={priceAdjustment}
                onChange={(e) => setPriceAdjustment(e.target.value)}
                className="w-20 h-9 text-sm"
                inputMode="decimal"
              />
              <Button
                size="sm"
                onClick={handlePriceAdjust}
                disabled={adjustingPrices || !priceAdjustment}
                data-testid="shipping-apply-price-adjust"
                className="bg-emerald-600 hover:bg-emerald-700 h-9 min-w-[80px]"
              >
                {adjustingPrices ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {adjustingPrices ? "Applying…" : "Apply"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const scopeMsg = selectedIds.length > 0 ? `${selectedIds.length} selected rate(s)` : `ALL shipping rates`;
                  if (!window.confirm(`Reset to original prices?\n\nScope: ${scopeMsg}\nOnly rates that were previously adjusted will be restored.`)) return;
                  try {
                    const res = await fetch(`${BACKEND_URL}/api/shipping/reset-prices`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ids: selectedIds.length > 0 ? selectedIds : null }),
                    });
                    const data = await res.json();
                    if (!res.ok) { toast.error(data.detail || "Reset failed"); return; }
                    toast.success(data.message || "Reset complete");
                    fetchShipping();
                  } catch (e) {
                    toast.error("Reset failed: " + e.message);
                  }
                }}
                data-testid="shipping-reset-prices-btn"
                className="gap-1.5 border-emerald-400 hover:bg-emerald-50 text-emerald-600 h-9"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reset Original</span>
                <span className="sm:hidden">Reset</span>
              </Button>
              {selectedIds.length > 0 ? (
                <span className="text-[11px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                  Scope: {selectedIds.length} selected
                </span>
              ) : (
                <span className="text-[11px] text-slate-500 whitespace-nowrap hidden sm:inline">
                  Scope: all {total.toLocaleString()} rates
                </span>
              )}
            </div>

            {selectedIds.length > 0 && (
              <>
                <Button size="sm" onClick={handleEditSelected} className="bg-blue-600 hover:bg-blue-700 gap-2 h-9">
                  <Edit3 className="w-4 h-4" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={triggerBulkDelete} className="gap-2 h-9">
                  <Trash2 className="w-4 h-4" /> Delete ({selectedIds.length})
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Shipping Rates"
        description={`You are about to delete ${deleteTarget?.count || 0} shipping rates. This action cannot be undone.`}
        onConfirm={executeBulkDelete}
        loading={deleting}
      />

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-muted-foreground">Loading rates...</p>
            </div>
          ) : shipping.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Truck className="w-16 h-16 text-slate-300" />
              <p className="text-slate-500 font-medium mt-4">No shipping rates found</p>
              <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="mt-4 gap-2">
                <Upload className="w-4 h-4" /> Upload Rates
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto max-h-[calc(100vh-380px)]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="p-3 w-10">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-slate-600 w-14">#</th>
                      <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort("shipment")}>
                        <div className="flex items-center gap-1">Service {renderSortIcon("shipment")}</div>
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort("country")}>
                        <div className="flex items-center gap-1">Country {renderSortIcon("country")}</div>
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort("weight_range")}>
                        <div className="flex items-center gap-1">Weight Range {renderSortIcon("weight_range")}</div>
                      </th>
                      <th className="p-3 text-right text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort("cost")}>
                        <div className="flex items-center justify-end gap-1">Cost (₹) {renderSortIcon("cost")}</div>
                      </th>
                      <th className="p-3 text-right text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort("suggested_usd_price")}>
                        <div className="flex items-center justify-end gap-1">USD Price {renderSortIcon("suggested_usd_price")}</div>
                      </th>
                      <th className="p-3 text-center text-xs font-semibold text-slate-600 uppercase w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shipping.map((rate, idx) => (
                      <tr key={rate.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.includes(rate.id) ? 'bg-blue-50' : ''}`}>
                        <td className="p-3">
                          <Checkbox checked={selectedIds.includes(rate.id)} onCheckedChange={(checked) => handleSelectItem(rate.id, checked)} />
                        </td>
                        <td className="p-3 text-slate-500 text-xs">{rate.serial_no || idx + 1}</td>
                        <td className="p-3 font-medium uppercase">{rate.shipment || "-"}</td>
                        <td className="p-3 uppercase">
                          {rate.country ? (
                            ['ALL', 'OTHER ALL'].includes((rate.country || '').toUpperCase().trim()) ? (
                              <span className="inline-flex items-center gap-1">
                                <span>{rate.country}</span>
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] font-semibold tracking-wide uppercase border border-amber-300"
                                  title="Catch-all wildcard — applies when no country-specific rate is found"
                                  data-testid={`wildcard-badge-${rate.id}`}
                                >
                                  Wildcard
                                </span>
                              </span>
                            ) : (
                              rate.country
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="p-3 font-mono text-xs">{rate.weight_range || "-"}</td>
                        <td className="p-3 text-right font-mono font-semibold text-emerald-700">{rate.cost || "-"}</td>
                        <td className="p-3 text-right font-mono text-slate-600">{rate.suggested_usd_price || "-"}</td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchRateHistory(rate.id, rate)}
                            className="h-7 w-7 p-0 hover:bg-indigo-50"
                            title="View Price History"
                            data-testid={`rate-history-${rate.id}`}
                          >
                            <Clock className="w-4 h-4 text-indigo-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t bg-slate-50">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Rows per page:</span>
                  <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                      <SelectItem value="500">500</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="ml-4">
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, total)} of {total.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 text-sm">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        if (!uploading) setShowUploadDialog(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-500" />
              Upload Shipping Price List
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file with shipping rates. Duplicates will be detected for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed border-slate-300 rounded-lg p-8 text-center transition-colors ${
                uploading ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50'
              }`}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              {uploading ? (
                <div className="space-y-4">
                  <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  
                  {/* Progress text */}
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-emerald-700">{uploadProgress}% Complete</p>
                    <p className="text-sm text-slate-600">{uploadProgressMessage}</p>
                  </div>
                  
                  {backgroundTaskId && (
                    <p className="text-xs text-slate-400">Processing in background - you can wait here for results</p>
                  )}
                </div>
              ) : (
                <>
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                  <p className="text-foreground font-medium">Click to upload or drag and drop</p>
                  <p className="text-sm text-muted-foreground mt-1">Excel files (.xlsx, .xls)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <div className="flex justify-between items-center pt-2 border-t">
              <Button variant="link" onClick={handleDownloadTemplate} className="text-sm p-0 text-emerald-600" disabled={uploading}>
                Download sample template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-500" />
              Price Changes Detected
            </DialogTitle>
            <DialogDescription>
              {uploadAnalysis?.duplicates_with_price_change} existing rates have different prices. Select which ones to update.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">New Rates</span>
                </div>
                <p className="text-2xl font-bold text-green-700 mt-1">{uploadAnalysis?.new_records || 0}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Price Changes</span>
                </div>
                <p className="text-2xl font-bold text-amber-700 mt-1">{uploadAnalysis?.duplicates_with_price_change || 0}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Selected to Update</span>
                </div>
                <p className="text-2xl font-bold text-blue-700 mt-1">{selectedDuplicates.length}</p>
              </div>
            </div>

            <div className="overflow-auto max-h-[350px] border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 w-10">
                      <Checkbox 
                        checked={selectedDuplicates.length === uploadAnalysis?.duplicate_details?.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDuplicates(uploadAnalysis.duplicate_details.map(d => d.id));
                          } else {
                            setSelectedDuplicates([]);
                          }
                        }}
                      />
                    </th>
                    <th className="p-2 text-left text-xs font-semibold">Service</th>
                    <th className="p-2 text-left text-xs font-semibold">Country</th>
                    <th className="p-2 text-left text-xs font-semibold">Weight</th>
                    <th className="p-2 text-right text-xs font-semibold">Old Cost</th>
                    <th className="p-2 text-center text-xs font-semibold w-10"></th>
                    <th className="p-2 text-right text-xs font-semibold">New Cost</th>
                    <th className="p-2 text-left text-xs font-semibold">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {uploadAnalysis?.duplicate_details?.map((dup) => {
                    const oldCost = parseFloat(dup.old_cost) || 0;
                    const newCost = parseFloat(dup.new_cost) || 0;
                    const diff = newCost - oldCost;
                    const isIncrease = diff > 0;
                    
                    return (
                      <tr key={dup.id} className={`${selectedDuplicates.includes(dup.id) ? 'bg-amber-50' : 'hover:bg-slate-50'}`}>
                        <td className="p-2">
                          <Checkbox 
                            checked={selectedDuplicates.includes(dup.id)}
                            onCheckedChange={(checked) => handleDuplicateSelect(dup.id, checked)}
                          />
                        </td>
                        <td className="p-2 font-medium uppercase">{dup.shipment}</td>
                        <td className="p-2 uppercase">{dup.country}</td>
                        <td className="p-2 font-mono text-xs">{dup.weight_range}</td>
                        <td className="p-2 text-right font-mono text-red-600 line-through">{dup.old_cost}</td>
                        <td className="p-2 text-center">
                          {isIncrease ? <ArrowUp className="w-4 h-4 text-red-500 mx-auto" /> : <ArrowDown className="w-4 h-4 text-green-500 mx-auto" />}
                        </td>
                        <td className="p-2 text-right font-mono font-semibold text-emerald-700">{dup.new_cost}</td>
                        <td className="p-2 text-xs text-slate-500">
                          {dup.last_updated ? formatDMY(dup.last_updated) : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => handleConfirmDuplicates(false)}>
              Skip Updates (Add New Only)
            </Button>
            <Button onClick={() => handleConfirmDuplicates(true)} className="bg-amber-600 hover:bg-amber-700 gap-2" disabled={selectedDuplicates.length === 0}>
              <CheckCircle2 className="w-4 h-4" /> Update Selected ({selectedDuplicates.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Panel Dialog */}
      <Dialog open={showErrorPanel} onOpenChange={setShowErrorPanel}>
        <DialogContent className="max-w-2xl max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Upload Errors ({uploadErrors.length})
            </DialogTitle>
            <DialogDescription>
              These entries could not be processed. Review and fix them in the source file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <div className="space-y-2">
              {uploadErrors.map((err, idx) => (
                <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      {err.sheet && <p className="text-sm font-medium text-red-800">Sheet: {err.sheet}</p>}
                      {(err.shipment || err.country || err.weight_range) && (
                        <p className="text-sm text-red-700">
                          {[err.shipment, err.country, err.weight_range].filter(Boolean).join(" • ")}
                        </p>
                      )}
                      <p className="text-sm text-red-600 mt-1">{err.error}</p>
                      <Badge variant="outline" className="mt-1 text-xs">{err.type || "error"}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => { setUploadErrors([]); setShowErrorPanel(false); }}>Clear Errors</Button>
            <Button onClick={() => setShowErrorPanel(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shipping Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-500" /> Add Shipping Rate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Service Type *</Label>
                <Input placeholder="e.g., RMS, EMS, ITPS" value={newShipping.shipment} onChange={(e) => setNewShipping(prev => ({ ...prev, shipment: e.target.value.toUpperCase() }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Country *</Label>
                <Input placeholder="e.g., Australia, USA" value={newShipping.country} onChange={(e) => setNewShipping(prev => ({ ...prev, country: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Weight Range</Label>
                <Input placeholder="e.g., 0-250g" value={newShipping.weight_range} onChange={(e) => setNewShipping(prev => ({ ...prev, weight_range: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Cost (₹)</Label>
                <Input type="number" placeholder="0" value={newShipping.cost} onChange={(e) => setNewShipping(prev => ({ ...prev, cost: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>USD Price</Label>
                <Input type="number" placeholder="0" value={newShipping.suggested_usd_price} onChange={(e) => setNewShipping(prev => ({ ...prev, suggested_usd_price: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddShipping} className="bg-blue-600 hover:bg-blue-700">Add Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shipping Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500" /> Edit Shipping Rate
            </DialogTitle>
          </DialogHeader>
          {editingShipping && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Service Type</Label>
                  <Input value={editingShipping.shipment || ""} onChange={(e) => setEditingShipping(prev => ({ ...prev, shipment: e.target.value.toUpperCase() }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input value={editingShipping.country || ""} onChange={(e) => setEditingShipping(prev => ({ ...prev, country: e.target.value.toUpperCase() }))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Weight Range</Label>
                  <Input value={editingShipping.weight_range || ""} onChange={(e) => setEditingShipping(prev => ({ ...prev, weight_range: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Cost (₹)</Label>
                  <Input type="number" value={editingShipping.cost || ""} onChange={(e) => setEditingShipping(prev => ({ ...prev, cost: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>USD Price</Label>
                  <Input type="number" value={editingShipping.suggested_usd_price || ""} onChange={(e) => setEditingShipping(prev => ({ ...prev, suggested_usd_price: e.target.value }))} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveShipping} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Price Adjustment Dialog */}
      <Dialog open={showBulkPriceDialog} onOpenChange={setShowBulkPriceDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-500" />
              Bulk Price Adjustment
            </DialogTitle>
            <DialogDescription>
              Increase or decrease shipping prices by percentage for a specific service or country.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden space-y-4">
            {/* Filters */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Service Type</Label>
                <Select 
                  value={bulkPriceData.service || "ALL"} 
                  onValueChange={(v) => setBulkPriceData(prev => ({ ...prev, service: v === "ALL" ? "" : v, preview: [], affectedCount: 0 }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Services</SelectItem>
                    {availableFilters.shipments.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Country</Label>
                <Select 
                  value={bulkPriceData.country || "ALL"} 
                  onValueChange={(v) => setBulkPriceData(prev => ({ ...prev, country: v === "ALL" ? "" : v, preview: [], affectedCount: 0 }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Countries</SelectItem>
                    {availableFilters.countries.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Percentage (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="e.g., 10 or -5"
                    value={bulkPriceData.percentage}
                    onChange={(e) => setBulkPriceData(prev => ({ ...prev, percentage: e.target.value, preview: [], affectedCount: 0 }))}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleBulkPricePreview} 
                    disabled={bulkPriceData.loading || !bulkPriceData.percentage}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {bulkPriceData.loading ? "..." : "Preview"}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Use positive for increase, negative for decrease</p>
              </div>
            </div>
            
            {/* Preview Results */}
            {bulkPriceData.affectedCount > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${parseFloat(bulkPriceData.percentage) > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'} px-3 py-1`}>
                      {parseFloat(bulkPriceData.percentage) > 0 ? (
                        <><TrendingUp className="w-4 h-4 mr-1 inline" /> +{bulkPriceData.percentage}% Increase</>
                      ) : (
                        <><TrendingDown className="w-4 h-4 mr-1 inline" /> {bulkPriceData.percentage}% Decrease</>
                      )}
                    </Badge>
                    <span className="text-sm text-slate-600">
                      <strong>{bulkPriceData.affectedCount.toLocaleString()}</strong> rates will be affected
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {bulkPriceData.service && <Badge variant="outline">{bulkPriceData.service}</Badge>}
                    {bulkPriceData.country && <Badge variant="outline">{bulkPriceData.country}</Badge>}
                  </div>
                </div>
                
                <div className="overflow-auto max-h-[300px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left text-xs font-semibold">Service</th>
                        <th className="p-2 text-left text-xs font-semibold">Country</th>
                        <th className="p-2 text-left text-xs font-semibold">Weight</th>
                        <th className="p-2 text-right text-xs font-semibold">Old Cost</th>
                        <th className="p-2 text-center text-xs font-semibold w-10"></th>
                        <th className="p-2 text-right text-xs font-semibold">New Cost</th>
                        <th className="p-2 text-right text-xs font-semibold">Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bulkPriceData.preview.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2 font-medium uppercase">{item.shipment}</td>
                          <td className="p-2 uppercase">{item.country}</td>
                          <td className="p-2 font-mono text-xs">{item.weight_range}</td>
                          <td className="p-2 text-right font-mono text-slate-600">{item.old_cost}</td>
                          <td className="p-2 text-center">
                            {parseFloat(bulkPriceData.percentage) > 0 ? (
                              <ArrowUp className="w-4 h-4 text-red-500 mx-auto" />
                            ) : (
                              <ArrowDown className="w-4 h-4 text-green-500 mx-auto" />
                            )}
                          </td>
                          <td className="p-2 text-right font-mono font-semibold text-emerald-700">{item.new_cost}</td>
                          <td className={`p-2 text-right font-mono text-xs ${item.change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {item.change > 0 ? '+' : ''}{item.change}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bulkPriceData.affectedCount > 100 && (
                    <div className="p-2 bg-amber-50 text-amber-700 text-xs text-center border-t">
                      Showing first 100 of {bulkPriceData.affectedCount.toLocaleString()} records
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Empty State */}
            {bulkPriceData.affectedCount === 0 && !bulkPriceData.loading && (
              <div className="text-center py-8 text-slate-500">
                <Calculator className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>Select filters and percentage, then click Preview to see affected rates</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => {
              setShowBulkPriceDialog(false);
              setBulkPriceData({ service: "", country: "", percentage: "", preview: [], affectedCount: 0, loading: false });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkPriceApply} 
              disabled={bulkPriceData.loading || bulkPriceData.affectedCount === 0}
              className={`gap-2 ${parseFloat(bulkPriceData.percentage) > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {bulkPriceData.loading ? "Applying..." : (
                <>
                  {parseFloat(bulkPriceData.percentage) > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  Apply to {bulkPriceData.affectedCount.toLocaleString()} Rates
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-500" />
              Price Change History
            </DialogTitle>
            <DialogDescription>
              View all shipping rate changes including bulk adjustments, uploads, and individual edits. Rollback bulk changes if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {historyLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-3 text-muted-foreground">Loading history...</p>
              </div>
            ) : priceHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Clock className="w-16 h-16 text-slate-300" />
                <p className="text-slate-500 font-medium mt-4">No price changes recorded yet</p>
                <p className="text-sm text-slate-400 mt-1">Price adjustments and uploads will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 overflow-auto max-h-[450px] pr-2">
                {priceHistory.map((entry) => {
                  const isBulk = entry.summary?.type === "bulk";
                  const isUpload = entry.summary?.type === "upload";
                  const isRollback = entry.details?.includes("ROLLBACK");
                  const percentage = entry.summary?.percentage || 0;
                  const isIncrease = percentage > 0;
                  
                  return (
                    <div
                      key={entry.id}
                      className={`p-4 rounded-lg border ${
                        isRollback 
                          ? 'bg-amber-50 border-amber-200' 
                          : isBulk 
                            ? isIncrease 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-green-50 border-green-200'
                            : isUpload
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isRollback
                              ? 'bg-amber-100'
                              : isBulk
                                ? isIncrease ? 'bg-red-100' : 'bg-green-100'
                                : isUpload
                                  ? 'bg-blue-100'
                                  : 'bg-slate-100'
                          }`}>
                            {isRollback ? (
                              <RotateCcw className="w-5 h-5 text-amber-600" />
                            ) : isBulk ? (
                              isIncrease ? <TrendingUp className="w-5 h-5 text-red-600" /> : <TrendingDown className="w-5 h-5 text-green-600" />
                            ) : isUpload ? (
                              <Upload className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Edit3 className="w-5 h-5 text-slate-600" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-foreground">
                                {isRollback ? "Rollback" : entry.action_display}
                              </span>
                              
                              {isBulk && (
                                <Badge className={`${isIncrease ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {isIncrease ? '+' : ''}{percentage}%
                                </Badge>
                              )}
                              
                              {isUpload && entry.summary?.inserted > 0 && (
                                <Badge className="bg-green-100 text-green-700">
                                  +{entry.summary.inserted} new
                                </Badge>
                              )}
                              
                              {isUpload && entry.summary?.updated > 0 && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  {entry.summary.updated} updated
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-sm text-muted-foreground mt-1">{entry.details}</p>
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" /> {entry.username}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatTimestamp(entry.timestamp)}
                              </span>
                              {entry.summary?.records_affected && (
                                <span className="font-medium">
                                  {entry.summary.records_affected.toLocaleString()} rates affected
                                </span>
                              )}
                            </div>
                            
                            {isBulk && !isRollback && (
                              <div className="flex items-center gap-2 mt-2">
                                {entry.summary?.service && entry.summary.service !== "All Services" && (
                                  <Badge variant="outline" className="text-xs">{entry.summary.service}</Badge>
                                )}
                                {entry.summary?.country && entry.summary.country !== "All Countries" && (
                                  <Badge variant="outline" className="text-xs">{entry.summary.country}</Badge>
                                )}
                              </div>
                            )}
                            
                            {/* Show hours remaining for deletable uploads */}
                            {isUpload && entry.summary?.can_delete && (
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs bg-red-50 border-red-200 text-red-600">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Delete available for {entry.summary.hours_remaining}h
                                </Badge>
                              </div>
                            )}
                            
                            {/* Show if upload was deleted */}
                            {isUpload && entry.summary?.is_deleted && (
                              <Badge className="mt-2 bg-slate-200 text-slate-600">
                                Deleted
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2">
                          {/* Rollback for bulk adjustments */}
                          {isBulk && !isRollback && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRollback(entry.id, entry)}
                              disabled={rollingBack === entry.id}
                              className="gap-1 border-amber-300 hover:bg-amber-50 text-amber-700"
                            >
                              {rollingBack === entry.id ? (
                                <>
                                  <div className="w-3 h-3 border border-amber-500 border-t-transparent rounded-full animate-spin" />
                                  Rolling back...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-3 h-3" />
                                  Rollback
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Delete for uploads within 48 hours */}
                          {isUpload && entry.summary?.can_delete && !entry.summary?.is_deleted && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUpload(entry.id, entry)}
                              disabled={deletingUpload === entry.id}
                              className="gap-1 border-red-300 hover:bg-red-50 text-red-700"
                            >
                              {deletingUpload === entry.id ? (
                                <>
                                  <div className="w-3 h-3 border border-red-500 border-t-transparent rounded-full animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-3 h-3" />
                                  Delete Upload
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Pagination */}
            {historyTotalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <span className="text-sm text-slate-600">
                  Showing {((historyPage - 1) * 20) + 1} - {Math.min(historyPage * 20, historyTotal)} of {historyTotal}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPriceHistory(historyPage - 1)}
                    disabled={historyPage === 1 || historyLoading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 text-sm">Page {historyPage} of {historyTotalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPriceHistory(historyPage + 1)}
                    disabled={historyPage === historyTotalPages || historyLoading}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => fetchPriceHistory(historyPage)} className="gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Price History Dialog */}
      <Dialog open={showRateHistoryDialog} onOpenChange={setShowRateHistoryDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              Price History
            </DialogTitle>
            {rateHistoryData?.rate && (
              <DialogDescription>
                <span className="font-semibold text-foreground">{rateHistoryData.rate.shipment}</span> • {rateHistoryData.rate.country} • {rateHistoryData.rate.weight_range}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {rateHistoryLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-muted-foreground">Loading price history...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current Price */}
              {rateHistoryData?.current && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-600 font-medium uppercase">Current Price</p>
                      <p className="text-2xl font-bold text-emerald-700">₹{rateHistoryData.current.cost}</p>
                      {rateHistoryData.current.suggested_usd_price && (
                        <p className="text-sm text-emerald-600">${rateHistoryData.current.suggested_usd_price} USD</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      <p className="text-xs text-slate-500 mt-1">
                        Since {formatDateShort(rateHistoryData.current.effective_from)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Price History */}
              {rateHistoryData?.history && rateHistoryData.history.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Previous Prices ({rateHistoryData.history.length})
                  </p>
                  <div className="max-h-64 overflow-auto space-y-2">
                    {rateHistoryData.history.map((h, idx) => (
                      <div key={h.id || idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-700">₹{h.cost}</p>
                          {h.suggested_usd_price && (
                            <p className="text-xs text-slate-500">${h.suggested_usd_price} USD</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <p>{formatDateShort(h.effective_from)}</p>
                          <p className="text-slate-400">to {formatDateShort(h.effective_to)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No previous prices recorded</p>
                  <p className="text-xs text-slate-400">Price history will appear here when rates change</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="pt-4 border-t">
            <Button onClick={() => setShowRateHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export/Print Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-teal-500" />
              Print / Export Shipping Rates
            </DialogTitle>
            <DialogDescription>
              Export shipping rates to Excel or PDF with optional price markup.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Export Format */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Export Format</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={exportConfig.format === "excel" ? "default" : "outline"}
                  onClick={() => setExportConfig(prev => ({ ...prev, format: "excel" }))}
                  className={`flex-1 gap-2 ${exportConfig.format === "excel" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </Button>
                <Button
                  type="button"
                  variant={exportConfig.format === "pdf" ? "default" : "outline"}
                  onClick={() => setExportConfig(prev => ({ ...prev, format: "pdf" }))}
                  className={`flex-1 gap-2 ${exportConfig.format === "pdf" ? "bg-red-600 hover:bg-red-700" : ""}`}
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Filter Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Data Selection</Label>
              <Select 
                value={exportConfig.filterType} 
                onValueChange={(v) => setExportConfig(prev => ({ ...prev, filterType: v, service: "", country: "" }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select what to export" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current View (with applied filters)</SelectItem>
                  <SelectItem value="selected" disabled={selectedIds.length === 0}>
                    Selected Rows Only ({selectedIds.length})
                  </SelectItem>
                  <SelectItem value="service">Filter by Service</SelectItem>
                  <SelectItem value="country">Filter by Country</SelectItem>
                  <SelectItem value="both">Filter by Service + Country</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Service Filter */}
            {(exportConfig.filterType === "service" || exportConfig.filterType === "both") && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Service / Shipment Type</Label>
                <Select 
                  value={exportConfig.service || "ALL"} 
                  onValueChange={(v) => setExportConfig(prev => ({ ...prev, service: v === "ALL" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Services</SelectItem>
                    {availableFilters.shipments.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Country Filter */}
            {(exportConfig.filterType === "country" || exportConfig.filterType === "both") && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Country</Label>
                <Select 
                  value={exportConfig.country || "ALL"} 
                  onValueChange={(v) => setExportConfig(prev => ({ ...prev, country: v === "ALL" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Countries</SelectItem>
                    {availableFilters.countries.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Price Increase */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Price Increase % (Optional)</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type="number"
                    placeholder="e.g., 10 for 10% markup"
                    value={exportConfig.increasePercent}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, increasePercent: e.target.value }))}
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to export original prices. Enter a value to add markup to all prices in the export.
              </p>
            </div>

            {/* Preview Info */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2 text-sm">
                <FileDown className="w-4 h-4 text-slate-500" />
                <span className="font-medium">Export Preview:</span>
              </div>
              <ul className="mt-2 text-xs text-slate-600 space-y-1">
                <li>• Format: {exportConfig.format === "excel" ? "Excel (.xlsx)" : "PDF Document"}</li>
                <li>• Data: {
                  exportConfig.filterType === "current" ? "Current view with filters" :
                  exportConfig.filterType === "selected" ? `${selectedIds.length} selected rows` :
                  exportConfig.filterType === "service" ? (exportConfig.service || "All services") :
                  exportConfig.filterType === "country" ? (exportConfig.country || "All countries") :
                  `${exportConfig.service || "All services"} + ${exportConfig.country || "All countries"}`
                }</li>
                {exportConfig.increasePercent && (
                  <li className="text-emerald-600 font-medium">• Price markup: +{exportConfig.increasePercent}%</li>
                )}
              </ul>
            </div>
          </div>
          
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={exportConfig.loading}
              className={`gap-2 ${exportConfig.format === "excel" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
            >
              {exportConfig.loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  {exportConfig.format === "excel" ? <FileSpreadsheet className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  Export {exportConfig.format === "excel" ? "Excel" : "PDF"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingPage;
